import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FRECUENCIAS, DIAS } from "@/lib/programados";

// v2.13.1 — Alta/edición de un RECORDATORIO en la ficha de la persona. Seis campos y nada más:
// qué recordar, con qué frecuencia, a qué hora(s), con cuántos minutos de aviso, desde y hasta.
// Lo técnico (zona IANA/DST, márgenes internos del motor, slots y estados del worker) vive en el
// backend y NO se le pregunta a la monitorista.
const vacio = {
  titulo: "", tipo: "diaria", horas: "07:00", dias: [], fecha: "",
  anticipacion_min: 0, desde: "", hasta: "",
};

function desdeTarea(t) {
  if (!t) return vacio;
  const r = t.regla || {};
  return {
    titulo: t.titulo || "", tipo: r.tipo || "diaria", horas: (r.horas || []).join(", "),
    dias: r.dias || [], fecha: r.fecha || "", anticipacion_min: t.anticipacion_min ?? 0,
    desde: r.desde || "", hasta: r.fin?.fecha_limite || "",
  };
}

export default function TareaProgramadaDialog({ open, onOpenChange, personaId, tarea, onSaved }) {
  const [f, setF] = useState(() => desdeTarea(tarea));
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editando = !!tarea;

  const guardar = async () => {
    const horas = String(f.horas).split(",").map((h) => h.trim()).filter(Boolean);
    const regla = {
      tipo: f.tipo, horas, dias: f.dias, fecha: f.fecha || null,
      desde: f.desde || null, fin: f.hasta ? { fecha_limite: f.hasta } : null,
    };
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/programados/tareas/${tarea.id}`, {
          if_version: tarea.version, titulo: f.titulo,
          anticipacion_min: Number(f.anticipacion_min), regla,
        });
      } else {
        await api.post("/programados/tareas", {
          persona_id: personaId, titulo: f.titulo,
          anticipacion_min: Number(f.anticipacion_min), regla,
        });
      }
      toast.success(editando ? "Recordatorio actualizado." : "Recordatorio creado.");
      await onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="programado-dialog">
        <DialogHeader>
          <DialogTitle>{editando ? "Editar recordatorio" : "Nuevo recordatorio"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Recordatorio</Label>
            <Input value={f.titulo} maxLength={80} placeholder="Tomar su pastilla"
              onChange={(e) => set("titulo", e.target.value)}
              className="rounded-xl mt-1" data-testid="programado-titulo" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Frecuencia</Label>
              <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="programado-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FRECUENCIAS.map((fr) => (
                    <SelectItem key={fr.value} value={fr.value} data-testid={`programado-tipo-${fr.value}`}>{fr.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Avisar (minutos antes)</Label>
              <Input type="number" min={0} max={120} value={f.anticipacion_min}
                onChange={(e) => set("anticipacion_min", e.target.value)}
                className="rounded-xl mt-1" data-testid="programado-anticipacion" />
              <p className="text-xs text-slate-400 mt-1">0 = a la hora exacta. La hora objetivo no cambia.</p>
            </div>
          </div>

          <div>
            <Label>Hora(s)</Label>
            <Input value={f.horas} onChange={(e) => set("horas", e.target.value)}
              placeholder="07:00, 15:00, 23:00" className="rounded-xl mt-1" data-testid="programado-horas" />
            <p className="text-xs text-slate-400 mt-1">Hora local del sitio. Varias horas separadas por coma.</p>
          </div>

          {f.tipo === "unica" && (
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={f.fecha} onChange={(e) => set("fecha", e.target.value)}
                className="rounded-xl mt-1" data-testid="programado-fecha" />
            </div>
          )}

          {f.tipo === "semanal" && (
            <div>
              <Label>Días</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DIAS.map((d, i) => (
                  <button key={d} type="button" data-testid={`programado-dia-${i}`}
                    onClick={() => set("dias", f.dias.includes(i) ? f.dias.filter((x) => x !== i) : [...f.dias, i])}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      f.dias.includes(i) ? "border-[#01CBE3] bg-[#01CBE3]/10 text-[#012472]" : "border-gray-200 text-slate-500"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {f.tipo !== "unica" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={f.desde} onChange={(e) => set("desde", e.target.value)}
                  className="rounded-xl mt-1" data-testid="programado-desde" />
              </div>
              <div>
                <Label>Hasta (opcional)</Label>
                <Input type="date" value={f.hasta} onChange={(e) => set("hasta", e.target.value)}
                  className="rounded-xl mt-1" data-testid="programado-hasta" />
                <p className="text-xs text-slate-400 mt-1">Vacío = sin fecha de fin.</p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}
            data-testid="programado-cancelar">Cancelar</Button>
          <Button className="rounded-xl bg-[#012472] hover:bg-[#012472]/90 text-white"
            disabled={guardando} aria-busy={guardando} onClick={guardar} data-testid="programado-guardar">
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
